import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const team = [
  {
    name: "沈亦文",
    role: "主编 · 历史与思想",
    bio: "长期从事文化史研究与写作，关注历史叙事中的边缘与遗忘。",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20professional%20editorial%20portrait%20of%20a%20thoughtful%20asian%20male%20writer%20in%20a%20soft%20beige%20studio%2C%20natural%20window%20light%2C%20muted%20brown%20tones%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-shen-01&orientation=squarish",
  },
  {
    name: "柳承安",
    role: "资深编辑 · 音乐",
    bio: "音乐评论人，专注爵士、灵魂乐与节奏文化的历史脉络。",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20professional%20editorial%20portrait%20of%20a%20calm%20asian%20female%20music%20critic%20in%20a%20soft%20beige%20studio%2C%20natural%20window%20light%2C%20muted%20brown%20tones%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-liu-02&orientation=squarish",
  },
  {
    name: "程未晚",
    role: "特约撰稿 · 视觉艺术",
    bio: "策展人与艺术写作者，关注当代视觉文化与手工艺的重新发现。",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20professional%20editorial%20portrait%20of%20a%20creative%20asian%20female%20curator%20in%20a%20soft%20beige%20studio%2C%20natural%20window%20light%2C%20muted%20brown%20tones%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-cheng-03&orientation=squarish",
  },
  {
    name: "何知远",
    role: "特约撰稿 · 社会观察",
    bio: "社会学背景，关注社区、教育与流行文化中的现实议题。",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20professional%20editorial%20portrait%20of%20an%20approachable%20asian%20male%20researcher%20in%20a%20soft%20beige%20studio%2C%20natural%20window%20light%2C%20muted%20brown%20tones%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-he-04&orientation=squarish",
  },
];

const values = [
  {
    icon: "ri-shield-star-line",
    title: "权威与严谨",
    text: "每一篇内容都经过核查与交叉印证，力求准确、可靠、可追溯。",
  },
  {
    icon: "ri-book-open-line",
    title: "深度与脉络",
    text: "拒绝碎片化，用专题与分类把信息编织成可阅读的知识结构。",
  },
  {
    icon: "ri-heart-3-line",
    title: "尊重与理解",
    text: "以平等、尊重的视角讲述文化，让复杂性被看见而不被简化。",
  },
  {
    icon: "ri-palette-line",
    title: "可读与美感",
    text: "兼顾学术深度与阅读体验，让知识既专业，也动人。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 黑人 - 权威黑人文化资讯平台",
    description:
      "了解「黑人」文化资讯平台的定位、内容范围、团队背景与合作方式。我们以内容权威性与文化深度为核心，提供系统化的黑人文化资讯。",
    keywords: "关于黑人,黑人文化平台,黑人文化编辑团队,内容合作,联系我们",
  });

  return (
    <div>
      <section className="bg-primary-600">
        <div className="mx-auto max-w-content px-4 md:px-8 py-12 md:py-16">
          <span className="text-xs font-medium tracking-[0.2em] text-accent-400">ABOUT US</span>
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-background-50">
            关于「黑人」
          </h1>
          <p className="mt-4 text-sm md:text-base text-primary-100 max-w-3xl leading-relaxed">
            一个以内容权威性与文化深度为核心的综合性文化资讯平台，专注于黑人文化、历史、艺术与社会动态的深度报道与多维呈现。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-content px-4 md:px-8 py-8 md:py-12">
        <div className="bg-background-50 rounded-lg border border-background-200 px-4 py-3 mb-10">
          <Breadcrumb items={[{ label: "关于我们" }]} />
        </div>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start mb-16">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-900">网站定位</h2>
            <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
              「黑人」面向关注黑人文化与社会发展的普通读者、研究者、艺术从业者与多元文化教育者。我们希望成为一个可以信赖的入口——既提供最新动态，也提供足够深的历史与文化语境。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              我们不追求信息的数量堆砌，而是坚持对每一个议题进行系统化的梳理。通过分类、标签与专题，帮助读者从任意一个兴趣点出发，逐步构建起完整的理解。
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((value) => (
                <div key={value.title} className="bg-background-100/70 rounded-lg p-5 border border-background-200">
                  <span className="w-10 h-10 rounded-md bg-background-50 text-primary-600 flex items-center justify-center">
                    <i className={`${value.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 font-heading text-base font-semibold text-foreground-900">
                    {value.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{value.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-background-100/70 rounded-lg border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-900">内容范围</h2>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              目前平台内容覆盖以下五个核心领域，并会持续扩展新的议题。
            </p>
            <div className="mt-6 space-y-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-start gap-4 p-4 rounded-md bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className={`${cat.icon} text-lg`} />
                  </span>
                  <div className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </span>
                    <span className="block mt-1 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      {cat.description}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-medium tracking-[0.2em] text-accent-600">OUR TEAM</span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              团队介绍
            </h2>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              由来自历史、音乐、艺术与社会研究领域的作者与编辑共同组成，坚持独立、严谨的内容生产。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((member) => (
              <div key={member.name} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                <div className="w-full h-56 bg-background-200">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    title={`${member.name} - 黑人文化资讯编辑团队`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-900">
                    {member.name}
                  </h3>
                  <p className="mt-1 text-xs text-accent-700">{member.role}</p>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-16">
          <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-7">
            <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
              <i className="ri-mail-line text-xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">联系我们</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              内容建议、纠错或一般咨询，欢迎通过邮件与我们取得联系。
            </p>
            <a
              href="mailto:contact@heiren-culture.example"
              className="mt-4 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-up-line" />
              contact@heiren-culture.example
            </a>
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-7">
            <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-links-line text-xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">合作渠道</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              欢迎媒体、机构与内容创作者洽谈转载、投稿与联合策划。
            </p>
            <p className="mt-4 text-sm text-foreground-600">
              合作邮箱：<span className="text-foreground-900">partner@heiren-culture.example</span>
            </p>
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-7">
            <span className="w-11 h-11 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
              <i className="ri-copyright-line text-xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">版权声明</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              本站为演示用途，所有文章、图片与数据均为虚拟示例内容，不代表真实人物或事件。
            </p>
            <p className="mt-4 text-xs text-foreground-500">© 2026 黑人 · 保留所有权利</p>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-medium tracking-[0.2em] text-accent-600">FEEDBACK</span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              意见反馈
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              你的每一条建议，都会帮助我们把这个平台做得更好。填写左侧表单即可提交，我们会在收到后认真阅读。
            </p>
            <ul className="mt-6 space-y-3">
              {["内容方向建议与选题推荐", "事实性错误的核查与纠正", "合作、投稿与转载洽谈", "功能体验与设计反馈"].map(
                (item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line text-accent-600 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                )
              )}
            </ul>
            <div className="mt-8 p-5 rounded-lg bg-background-100/70 border border-background-200">
              <p className="text-xs text-foreground-500 leading-relaxed">
                说明：本表单仅用于演示提交效果，提交后会显示感谢提示。请勿填写真实敏感信息。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FeedbackForm />
          </div>
        </section>
      </div>
    </div>
  );
}